import React, { useState, useEffect } from 'react';
import { 
  Bell, Send, Clock, Users, AlertTriangle, CheckCircle, 
  RefreshCw, Mail, Calendar, Crown, Loader2 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminTrialReminders = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [selectedDays, setSelectedDays] = useState(3);
  const { toast } = useToast();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/premium/admin/trial-expiry-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        throw new Error('Failed to load stats');
      }
    } catch (error) {
      console.error('Failed to load trial stats:', error);
      toast({
        title: "Error",
        description: "Failed to load trial expiry statistics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/premium/admin/send-trial-reminders?days_before=${selectedDays}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Reminders Sent!",
          description: `Successfully sent ${data.sent} reminder emails. ${data.failed} failed.`,
        });
        // Reload stats
        loadStats();
      } else {
        throw new Error(data.detail || 'Failed to send reminders');
      }
    } catch (error) {
      console.error('Failed to send reminders:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send reminder emails",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleResetFlags = async () => {
    if (!window.confirm('Are you sure you want to reset all reminder flags? This will allow re-sending reminders to all users.')) {
      return;
    }
    
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/premium/admin/reset-reminder-flags`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Flags Reset",
          description: `Reset reminder flags for ${data.modifiedCount} users.`,
        });
        loadStats();
      } else {
        throw new Error(data.detail || 'Failed to reset flags');
      }
    } catch (error) {
      console.error('Failed to reset flags:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to reset reminder flags",
        variant: "destructive"
      });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trial Expiry Reminders</h1>
          <p className="text-gray-600">Automatic email reminders for expiring free trials</p>
        </div>
        <Button 
          onClick={loadStats} 
          variant="outline" 
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Auto-Send Status Banner */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-green-900">✅ Automatic Reminders Active</h3>
            <p className="text-green-700 text-sm mt-1">
              The system automatically sends email reminders to users:
            </p>
            <ul className="text-green-700 text-sm mt-2 space-y-1">
              <li>• <strong>3 days</strong> before trial expires</li>
              <li>• <strong>1 day</strong> before trial expires</li>
            </ul>
            <p className="text-green-600 text-xs mt-2">
              Emails are sent daily at 9:00 AM UTC. No manual action required.
            </p>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Crown className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalTrialUsers || 0}</p>
                <p className="text-sm text-gray-500">Total Trial Users</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{stats?.expiring1Day?.count || 0}</p>
                <p className="text-sm text-red-600">Expiring Tomorrow</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats?.expiring3Days?.count || 0}</p>
                <p className="text-sm text-amber-600">Expiring in 2-3 Days</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats?.expiring7Days?.count || 0}</p>
                <p className="text-sm text-green-600">Expiring in 4-7 Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Send Reminders Card - Manual Override */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Manual Override (Optional)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Reminders are sent automatically. Use this only if you need to 
              manually trigger reminders for a specific group of users.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Send to users expiring within:
              </label>
              <select
                value={selectedDays}
                onChange={(e) => setSelectedDays(Number(e.target.value))}
                className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mint-500"
              >
                <option value={1}>1 day (urgent)</option>
                <option value={3}>3 days (recommended)</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
              </select>
            </div>
            
            <Button
              onClick={handleSendReminders}
              disabled={sending}
              className="bg-mint-500 hover:bg-mint-600"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reminders
                </>
              )}
            </Button>
          </div>

          <div className="pt-4 border-t">
            <Button
              onClick={handleResetFlags}
              disabled={resetting}
              variant="outline"
              size="sm"
              className="text-gray-600"
            >
              {resetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Reminder Flags
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 mt-2">
              Reset flags to allow re-sending reminders to users who already received one
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Users Expiring Soon */}
      {stats?.expiring1Day?.users?.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="bg-red-50">
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Expiring Tomorrow ({stats.expiring1Day.count})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {stats.expiring1Day.users.map((user) => (
                <UserRow key={user.id} user={user} urgent />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats?.expiring3Days?.users?.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="bg-amber-50">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Clock className="w-5 h-5" />
              Expiring in 2-3 Days ({stats.expiring3Days.count})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {stats.expiring3Days.users.map((user) => (
                <UserRow key={user.id} user={user} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stats?.expiring7Days?.users?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Expiring in 4-7 Days ({stats.expiring7Days.count})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {stats.expiring7Days.users.map((user) => (
                <UserRow key={user.id} user={user} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No users */}
      {stats?.totalTrialUsers === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Crown className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">No Free Trial Users</h3>
            <p className="text-gray-500">There are currently no users on free trial.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// User Row Component
const UserRow = ({ user, urgent = false }) => {
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={`flex items-center justify-between p-4 ${urgent ? 'bg-red-50' : ''}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          urgent ? 'bg-red-100' : 'bg-gray-100'
        }`}>
          <Users className={`w-5 h-5 ${urgent ? 'text-red-600' : 'text-gray-600'}`} />
        </div>
        <div>
          <p className="font-medium text-gray-900">{user.firstName}</p>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-right">
          <p className={`text-sm font-medium ${urgent ? 'text-red-600' : 'text-gray-700'}`}>
            {user.daysRemaining} day{user.daysRemaining !== 1 ? 's' : ''} left
          </p>
          <p className="text-xs text-gray-500">Expires {formatDate(user.expiryDate)}</p>
        </div>
        {user.reminderSent ? (
          <Badge className="bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Reminded
          </Badge>
        ) : (
          <Badge variant="outline" className="text-gray-500">
            Not Reminded
          </Badge>
        )}
      </div>
    </div>
  );
};

export default AdminTrialReminders;
